import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { brandService } from '../../services/brandService';
import { Brand, BrandStats } from '../../types';

interface BrandState {
  currentBrand: Brand | null;
  brandStats: BrandStats | null;
  brands: Brand[]; // For super admin
  brandUsers: any[]; // Users assigned to a specific brand
  availableUsers: any[]; // Users available for assignment
  pagination: {
    brands: { page: number; pageSize: number; total: number; pageCount: number };
    brandUsers: { page: number; pageSize: number; total: number; pageCount: number };
    availableUsers: { page: number; pageSize: number; total: number; pageCount: number };
  };
  userLimit: {
    currentUsers: number;
    maxUsers: number;
    canAddUser: boolean;
  } | null;
  invoiceLimit: {
    currentInvoices: number;
    maxInvoices: number;
    canCreateInvoice: boolean;
  } | null;
  subscriptionDetails: {
    subscriptionPlan: string;
    subscriptionExpiry: string;
    daysRemaining: number;
    isActive: boolean;
  } | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: BrandState = {
  currentBrand: null,
  brandStats: null,
  brands: [],
  brandUsers: [],
  availableUsers: [],
  pagination: {
    brands: { page: 1, pageSize: 10, total: 0, pageCount: 0 },
    brandUsers: { page: 1, pageSize: 10, total: 0, pageCount: 0 },
    availableUsers: { page: 1, pageSize: 10, total: 0, pageCount: 0 },
  },
  userLimit: null,
  invoiceLimit: null,
  subscriptionDetails: null,
  isLoading: false,
  error: null,
};

// Brand Management Thunks
export const fetchBrand = createAsyncThunk(
  'brand/fetchBrand',
  async (id: number, { rejectWithValue }) => {
    try {
      console.log('fetchBrand: Making API call for brand ID:', id);
      console.log('fetchBrand: Brand ID type:', typeof id);
      console.log('fetchBrand: Brand ID stringified:', JSON.stringify(id));
      
      // Validate that id is actually a number
      if (typeof id !== 'number' || isNaN(id)) {
        console.error('fetchBrand: Invalid brand ID provided:', id);
        return rejectWithValue('Invalid brand ID provided');
      }
      
      const response = await brandService.getBrand(id);
      console.log('fetchBrand: API response:', response);
      return response.data;
    } catch (error: any) {
      console.error('fetchBrand: API error:', error);
      console.error('fetchBrand: Error response:', error.response);
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch brand');
    }
  }
);



export const updateBrand = createAsyncThunk(
  'brand/updateBrand',
  async ({ id, data }: { id: number; data: Partial<Brand['attributes']> }, { rejectWithValue }) => {
    try {
      const response = await brandService.updateBrand(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update brand');
    }
  }
);

export const fetchBrands = createAsyncThunk(
  'brand/fetchBrands',
  async (params: { page?: number; pageSize?: number; search?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await brandService.getBrands(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch brands');
    }
  }
);

// Brand User Management Thunks
export const fetchBrandUsers = createAsyncThunk(
  'brand/fetchBrandUsers',
  async ({ brandId, params = {} }: { brandId: number; params?: { page?: number; pageSize?: number; search?: string } }, { rejectWithValue }) => {
    try {
      const response = await brandService.getBrandUsers(brandId, params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch brand users');
    }
  }
);

export const fetchAvailableUsers = createAsyncThunk(
  'brand/fetchAvailableUsers',
  async (params: { page?: number; pageSize?: number; search?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await brandService.getAvailableUsers(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch available users');
    }
  }
);

export const assignUserToBrand = createAsyncThunk(
  'brand/assignUserToBrand',
  async ({ brandId, userId }: { brandId: number; userId: number }, { rejectWithValue }) => {
    try {
      const response = await brandService.assignUserToBrand(brandId, userId);
      return { brandId, userId, ...response };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to assign user to brand');
    }
  }
);

export const removeUserFromBrand = createAsyncThunk(
  'brand/removeUserFromBrand',
  async ({ brandId, userId }: { brandId: number; userId: number }, { rejectWithValue }) => {
    try {
      const response = await brandService.removeUserFromBrand(brandId, userId);
      return { brandId, userId, ...response };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to remove user from brand');
    }
  }
);

// Brand Statistics
export const fetchBrandStats = createAsyncThunk(
  'brand/fetchBrandStats',
  async (id: number, { rejectWithValue }) => {
    try {
      console.log('fetchBrandStats: Making API call for brand ID:', id);
      console.log('fetchBrandStats: Brand ID type:', typeof id);
      console.log('fetchBrandStats: Brand ID stringified:', JSON.stringify(id));
      
      // Validate that id is actually a number
      if (typeof id !== 'number' || isNaN(id)) {
        console.error('fetchBrandStats: Invalid brand ID provided:', id);
        return rejectWithValue('Invalid brand ID provided');
      }
      
      const response = await brandService.getBrandStats(id);
      console.log('fetchBrandStats: API response:', response);
      return response.data;
    } catch (error: any) {
      console.error('fetchBrandStats: API error:', error);
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch brand stats');
    }
  }
);

export const updateSubscription = createAsyncThunk(
  'brand/updateSubscription',
  async ({ id, subscriptionData }: { 
    id: number; 
    subscriptionData: {
      subscriptionPlan: string;
      subscriptionExpiry: string;
      maxUsers: number;
      maxInvoicesPerMonth: number;
    }
  }, { rejectWithValue }) => {
    try {
      const response = await brandService.updateSubscription(id, subscriptionData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update subscription');
    }
  }
);

// Upload Brand Logo
export const uploadBrandLogo = createAsyncThunk(
  'brand/uploadBrandLogo',
  async ({ brandId, file }: { brandId: number; file: File }, { rejectWithValue }) => {
    try {
      const response = await brandService.uploadBrandLogo(brandId, file);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to upload brand logo');
    }
  }
);

const brandSlice = createSlice({
  name: 'brand',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentBrand: (state, action: PayloadAction<Brand | null>) => {
      state.currentBrand = action.payload;
    },
    clearBrandData: (state) => {
      state.currentBrand = null;
      state.brandStats = null;
      state.userLimit = null;
      state.invoiceLimit = null;
      state.subscriptionDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Brand
      .addCase(fetchBrand.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBrand.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentBrand = action.payload;
        
        // Extract data from populated brand response
        const brand = action.payload;
        
        // Set subscription details from brand data
        if (brand) {
          // Handle both flat and attributes structure
          const brandData = (brand as any).attributes || (brand as any);
          
          state.subscriptionDetails = {
            subscriptionPlan: brandData.subscriptionPlan || 'Basic',
            subscriptionExpiry: brandData.subscriptionExpiry || '',
            daysRemaining: brandData.daysRemaining || 0,
            isActive: brandData.isActive || false,
          };
          
          // Set user limits from brand data
          state.userLimit = {
            currentUsers: brandData.currentUsers || 0,
            maxUsers: brandData.maxUsers || 0,
            canAddUser: (brandData.currentUsers || 0) < (brandData.maxUsers || 0),
          };
          
          // Set invoice limits from brand data
          state.invoiceLimit = {
            currentInvoices: brandData.currentInvoices || 0,
            maxInvoices: brandData.maxInvoicesPerMonth || 0,
            canCreateInvoice: (brandData.currentInvoices || 0) < (brandData.maxInvoicesPerMonth || 0),
          };
        }
      })
      .addCase(fetchBrand.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })



      // Update Brand
      .addCase(updateBrand.fulfilled, (state, action) => {
        state.currentBrand = action.payload;
      })

      // Fetch Brands (for super admin)
      .addCase(fetchBrands.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.isLoading = false;
        state.brands = action.payload.data || [];
        state.pagination.brands = {
          page: action.payload.meta?.pagination?.page || 1,
          pageSize: action.payload.meta?.pagination?.pageSize || 10,
          total: action.payload.meta?.pagination?.total || 0,
          pageCount: action.payload.meta?.pagination?.pageCount || 0,
        };
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Brand Users
      .addCase(fetchBrandUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBrandUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.brandUsers = action.payload.data || [];
        state.pagination.brandUsers = {
          page: action.payload.meta?.pagination?.page || 1,
          pageSize: action.payload.meta?.pagination?.pageSize || 10,
          total: action.payload.meta?.pagination?.total || 0,
          pageCount: action.payload.meta?.pagination?.pageCount || 0,
        };
      })
      .addCase(fetchBrandUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Available Users
      .addCase(fetchAvailableUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAvailableUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.availableUsers = action.payload.data || [];
        state.pagination.availableUsers = {
          page: action.payload.meta?.pagination?.page || 1,
          pageSize: action.payload.meta?.pagination?.pageSize || 10,
          total: action.payload.meta?.pagination?.total || 0,
          pageCount: action.payload.meta?.pagination?.pageCount || 0,
        };
      })
      .addCase(fetchAvailableUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Assign User to Brand
      .addCase(assignUserToBrand.fulfilled, (state, action) => {
        // Refresh will be handled by dispatching fetchBrandUsers again
        state.error = null;
      })
      .addCase(assignUserToBrand.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Remove User from Brand
      .addCase(removeUserFromBrand.fulfilled, (state, action) => {
        // Refresh will be handled by dispatching fetchBrandUsers again
        state.error = null;
      })
      .addCase(removeUserFromBrand.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Brand Stats
      .addCase(fetchBrandStats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBrandStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.brandStats = action.payload;
      })
      .addCase(fetchBrandStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })



      // Update Subscription
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.subscriptionDetails = action.payload;
        if (state.currentBrand) {
          state.currentBrand.attributes.subscriptionPlan = action.payload.subscriptionPlan;
          state.currentBrand.attributes.subscriptionExpiry = action.payload.subscriptionExpiry;
          state.currentBrand.attributes.maxUsers = action.payload.maxUsers;
          state.currentBrand.attributes.maxInvoicesPerMonth = action.payload.maxInvoicesPerMonth;
        }
      })

      // Upload Brand Logo
      .addCase(uploadBrandLogo.fulfilled, (state, action) => {
        if (state.currentBrand && action.payload.length > 0) {
          state.currentBrand.attributes.logo = action.payload[0].url;
        }
      });
  },
});

export const { clearError, setCurrentBrand, clearBrandData } = brandSlice.actions;
export default brandSlice.reducer; 